// Cliente HTTP del panel de super-admin contra apps/api.
//
// La URL base se inyecta en build vía VITE_SUPER_ADMIN_API. NO se hardcodea
// ningún dominio: así la SPA se puede deployar a cualquier subdominio sin
// recompilar lógica. Default de desarrollo: http://localhost:3000.
//
// Todos los requests llevan credentials: 'include' para que la cookie de
// sesión del super-admin (inventario_sa_session, httpOnly) viaje al API.
// En dev la SPA corre en :5174 y el API en :3000 — el navegador lo trata
// como cross-site, así que el backend debe emitir la cookie con
// SameSite=None; Secure.

export const API_BASE: string =
  import.meta.env.VITE_SUPER_ADMIN_API ?? 'http://localhost:3000';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Error de red / fetch que nunca llegó al servidor (distinto de un 4xx/5xx). */
export class NetworkError extends Error {
  constructor(message = 'No se pudo conectar con el servidor') {
    super(message);
    this.name = 'NetworkError';
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
}

/** Saca un mensaje legible del cuerpo de un error del API (NestJS: { message }). */
function extractMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object' && 'message' in body) {
    const msg = (body as { message: unknown }).message;
    if (typeof msg === 'string' && msg.trim() !== '') return msg;
    // NestJS validation errors devuelven message como string[].
    if (Array.isArray(msg) && typeof msg[0] === 'string') return msg[0];
  }
  return `Error ${status}`;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body } = opts;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    // fetch rechaza solo por fallo de red / CORS / DNS — nunca por status HTTP.
    throw new NetworkError();
  }

  // 204 / cuerpo vacío: no intentar parsear JSON.
  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, extractMessage(parsed, res.status), parsed);
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body }),
};
